import styled from '@emotion/styled';
import { Skeleton, tokens, Typography } from '@sunrun/experience-ui-components';
import { useState } from 'react';
import { NewsFeedItem, useNewsFeed } from 'hooks/useNewsFeed';
import { HorizontalWidgetScrollerContainer } from 'components/atoms/HorizontalWidgetScrollerContainer';
import { NewDashboardOrgType } from 'helpers/repTypeHelper';
import { formatDateReadable } from 'helpers/formatHelpers';

type NewsFeedProps = {
  orgType: NewDashboardOrgType;
};

const NewsFeed = ({ orgType }: NewsFeedProps) => {
  const { newsFeed, isNewsFeedLoading, newsFeedError, isNewsFeedLoadedEmpty } =
    useNewsFeed(orgType);

  const showStatusMessage =
    !isNewsFeedLoading && (newsFeedError != null || isNewsFeedLoadedEmpty);

  const siteNameMap = {
    'Executive Corner':
      'https://current.sunrun.com/site/9f52d804-a780-53c3-a950-a49a1b84c840/dashboard',
    RunX: 'https://current.sunrun.com/site/67ce0605-02e4-56d0-9051-8cf88af60980/dashboard',
  };

  const navigateToSite = (siteName: string) => {
    if (!siteName || !siteNameMap[siteName as keyof typeof siteNameMap]) {
      return;
    }
    window.open(siteNameMap[siteName as keyof typeof siteNameMap], '_blank');
  };

  return (
    <HorizontalWidgetScrollerContainer>
      <>
        {isNewsFeedLoading &&
          [1, 2, 3].map((item) => <StyledSkeleton key={item} />)}
        {showStatusMessage && (
          <StatusMessage role="status">
            <Typography
              size="small"
              color={
                newsFeedError ? tokens.ERROR_20 : tokens.TINTS_OFF_WHITE_20
              }>
              {newsFeedError ??
                'No news items are available right now. Check back later.'}
            </Typography>
          </StatusMessage>
        )}
        {!isNewsFeedLoading &&
          !newsFeedError &&
          newsFeed.map((item) => (
            <NewsFeedItemContainer key={item.url}>
              <NewsFeedItemImageWithFallback
                src={item.coverImgPublicUrl || ''}
                handleClick={() => window.open(item.url, '_blank')}
              />
              <DescriptionContainer>
                <TitleClamp
                  title={item.title}
                  onClick={() => window.open(item.url, '_blank')}>
                  <TypographyWithLineHeight>
                    {item.title}
                  </TypographyWithLineHeight>
                </TitleClamp>
                <FlexDiv>
                  <Typography
                    size="extra_small"
                    color={tokens.TINTS_OFF_WHITE_20}>
                    In{' '}
                    {item.site?.siteName &&
                    item.site?.siteName in siteNameMap ? (
                      <StyledLink
                        onClick={() => navigateToSite(item.site?.siteName)}>
                        {item.site?.siteName}
                      </StyledLink>
                    ) : (
                      item.site?.siteName
                    )}
                  </Typography>
                  <Typography
                    size="extra_small"
                    color={tokens.TINTS_OFF_WHITE_20}>
                    {formatDateReadable(item.publishAt)}
                  </Typography>
                </FlexDiv>
              </DescriptionContainer>
            </NewsFeedItemContainer>
          ))}
      </>
    </HorizontalWidgetScrollerContainer>
  );
};

const FALLBACK_IMAGE_SRC = '/Sunrun_Logo.svg';

const NewsFeedItemImageWithFallback = ({
  src,
  handleClick,
}: {
  src: string;
  handleClick: () => void;
}) => {
  const [hasError, setHasError] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const showFallback = !src || hasError || !hasLoaded;

  if (!src) {
    return (
      <NewsFeedItemImageWrapper onClick={handleClick}>
        <img src={FALLBACK_IMAGE_SRC} alt="" aria-hidden />
      </NewsFeedItemImageWrapper>
    );
  }

  return (
    <NewsFeedItemImageWrapper onClick={handleClick}>
      {showFallback && (
        <NewsFeedImage
          showFallback
          src={FALLBACK_IMAGE_SRC}
          alt=""
          aria-hidden
          className="fallback-image"
        />
      )}
      <NewsFeedImage
        showFallback={showFallback}
        src={src}
        alt=""
        aria-hidden
        onLoad={() => setHasLoaded(true)}
        onError={() => setHasError(true)}
      />
    </NewsFeedItemImageWrapper>
  );
};

const DescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const StatusMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 100%;
  height: 100%;
  padding: 16px 24px;
  box-sizing: border-box;
  text-align: center;
`;

const StyledSkeleton = styled(Skeleton)`
  width: 275px;
  min-width: 275px;
  height: 100%;
`;

const FlexDiv = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledLink = styled.span`
  cursor: pointer;
  text-decoration: none;
  :hover {
    text-decoration: underline;
    color: ${tokens.BRAND_HERO_BLUE};
  }
`;

const NewsFeedImage = styled.img<{ showFallback: boolean }>`
  display: ${({ showFallback }: { showFallback: boolean }) =>
    showFallback ? 'none' : 'block'};
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  aspect-ratio: 4/3;
`;

const NewsFeedItemContainer = styled.div`
  padding: 16px 12px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 10px;
  width: 275px;
  min-width: 275px;
  box-sizing: border-box;
  border: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  background-color: ${tokens.WHITE};
  border-radius: 8px;
`;

const NewsFeedItemImageWrapper = styled.div`
  width: 250px;
  aspect-ratio: 4/3;
  border-radius: 8px;
  background-color: ${tokens.TINTS_OFF_WHITE_40};
  overflow: hidden;
  position: relative;
  cursor: pointer;

  .fallback-image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
  }
`;

const TitleClamp = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  cursor: pointer;
`;

const TypographyWithLineHeight = styled(Typography)`
  line-height: 20px !important;
`;

export { NewsFeed };
export type { NewsFeedItem };
